<template>
  <div class="module-list">
    <b-card>
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center">
          <h4 class="card-title mb-0">Modules</h4>
          <b-button
            variant="primary"
            @click="openAddModal"
          >
            <feather-icon icon="PlusIcon" class="mr-50" />
            Create New Module
          </b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="primary" />
          <p class="mt-2">Loading...</p>
        </div>

        <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = ''">
          {{ error }}
        </b-alert>

        <div v-if="!loading && !error">
          <b-table
            v-if="modules.length > 0"
            :items="modules"
            :fields="fields"
            responsive
            striped
            hover
            :busy="loading"
          >
            <template #cell(routes)="data">
              <b-badge variant="info">{{ data.value ? data.value.length : 0 }}</b-badge>
            </template>

            <template #cell(actions)="data">
              <b-button
                variant="primary"
                size="sm"
                @click="openEditModal(data.item.id)"
                class="mr-1"
              >
                <feather-icon icon="EditIcon" size="14" />
                Edit
              </b-button>
            </template>

            <template #table-busy>
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>
          </b-table>

          <div v-else class="text-center py-5">
            <p class="text-muted">No modules found</p>
            <b-button
              variant="primary"
              @click="openAddModal"
            >
              Create Your First Module
            </b-button>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Module Form Modal -->
    <b-modal
      id="module-form-modal"
      v-model="modalVisible"
      :title="selectedModuleId ? 'Edit Module' : 'Create Module'"
      size="lg"
      cancel-variant="outline-secondary"
      ok-title="Submit"
      cancel-title="Close"
      hide-footer
      @hidden="handleModalClose"
    >
      <ModuleForm
        ref="moduleForm"
        :is-modal="true"
        :module-id="selectedModuleId"
        :close-pop-up="handleModalClose"
        :get-grid-data="loadModules"
      />
      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="secondary"
          @click="handleModalClose"
          class="mr-1"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="submitForm"
          :disabled="formLoading"
        >
          <b-spinner v-if="formLoading" small class="mr-50" />
          {{ selectedModuleId ? 'Update' : 'Create' }} Module
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { moduleService } from '@/services/moduleService';
import ModuleForm from './ModuleForm.vue';
import {
  BCard,
  BCardHeader,
  BCardBody,
  BCardTitle,
  BButton,
  BTable,
  BSpinner,
  BBadge,
  BAlert,
  BModal,
} from 'bootstrap-vue';
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue';

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardTitle,
    BButton,
    BTable,
    BSpinner,
    BBadge,
    BAlert,
    BModal,
    ModuleForm,
    FeatherIcon,
  },
  data() {
    return {
      modules: [],
      loading: false,
      error: '',
      modalVisible: false,
      selectedModuleId: null,
      formLoading: false,
      fields: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
        },
        {
          key: 'name',
          label: 'Name',
          sortable: true,
        },
        {
          key: 'sid',
          label: 'Software ID',
          sortable: true,
        },
        {
          key: 'routes',
          label: 'Routes Count',
          sortable: false,
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
        },
      ],
    };
  },
  mounted() {
    this.loadModules();
  },
  methods: {
    async loadModules() {
      try {
        this.loading = true;
        this.error = '';
        const response = await moduleService.getAllModules();
        if (response.data) {
          this.modules = Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data)
            ? response.data
            : [];
        } else {
          this.modules = [];
        }
      } catch (err) {
        this.error = err.message || 'Failed to load modules';
        this.modules = [];
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.selectedModuleId = null;
      this.modalVisible = true;
      // Reset form when opening add modal
      this.$nextTick(() => {
        if (this.$refs.moduleForm) {
          this.$refs.moduleForm.resetForm();
        }
      });
    },
    openEditModal(moduleId) {
      this.selectedModuleId = moduleId;
      this.modalVisible = true;
    },
    handleModalClose() {
      this.modalVisible = false;
      this.selectedModuleId = null;
      // Reset form when closing modal
      this.$nextTick(() => {
        if (this.$refs.moduleForm) {
          this.$refs.moduleForm.resetForm();
        }
      });
    },
    async submitForm() {
      // Trigger form submission in ModuleForm component
      if (this.$refs.moduleForm) {
        this.formLoading = true;
        try {
          const success = await this.$refs.moduleForm.handleSubmit();
          // If successful, the form will handle closing the modal
        } catch (error) {
          console.error('Error submitting form:', error);
        } finally {
          this.formLoading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.module-list {
  padding: 20px;
}
</style>

